"use client";

import {
	FastForwardIcon,
	MapIcon,
	MapPinIcon,
	MinusIcon,
	PlusIcon,
} from "lucide-react";
import { cn } from "../../../lib";
import { Button } from "../../button";
import {
	OctagonButton,
	type OctagonButtonHoverVariant,
} from "../../octagon-button";
import { Slider } from "../../slider";
import {
	HEXAGON_MAP_MAX_USER_ZOOM,
	HEXAGON_MAP_MIN_USER_ZOOM,
	HEXAGON_MAP_ZOOM_SLIDER_STEP,
} from "../lib/map-overlay-gap";
import { HexagonMapCircleActionButton } from "./HexagonMapCircleActionButton";
import {
	ThumbMovementControls,
	type ThumbMovementControlsProps,
} from "./ThumbMovementControls";

export interface HexagonMapAuthoringBiomeOption {
	value: string;
	label: string;
	/** When false, option is disabled in the previewer dropdown. */
	enabled?: boolean;
	comingSoonLabel?: string;
}

export interface HexagonMapCornerOctagonButtonConfig {
	ariaLabel: string;
	badge: string;
	iconSrc: string;
	onClick?: () => void;
	disabled?: boolean;
	active?: boolean;
	hoverVariant?: OctagonButtonHoverVariant;
}

interface HexagonMapControlsProps {
	userZoom: number;
	onZoomIn: () => void;
	onZoomOut: () => void;
	onZoomChange: (value: number) => void;
	/** When true, controls sit below the scoped preview dim overlay and are inert. */
	isScopedPanelOpen?: boolean;
	/** Center viewport on the path character (enabled when zoomed in). */
	onCenterOnPlayer?: () => void;
	canCenterOnPlayer?: boolean;
	/** Zoom out to the default map overview when centered and zoomed in. */
	onReturnToMapView?: () => void;
	canReturnToMapView?: boolean;
	/** Walk the character to the furthest unlocked stop on the path. */
	onFastForwardToFurthestStop?: () => void;
	canFastForwardToFurthestStop?: boolean;
	topLeftOctagonButton?: HexagonMapCornerOctagonButtonConfig;
	topRightOctagonButton?: HexagonMapCornerOctagonButtonConfig;
	thumbMovementControls?: Pick<
		ThumbMovementControlsProps,
		| "disabled"
		| "onDriveChange"
		| "onDriveEnd"
		| "onPointerInteractionChange"
		| "syncedVisual"
	>;
}

function renderCornerOctagonButton(
	config: HexagonMapCornerOctagonButtonConfig | undefined,
) {
	if (config == null) {
		return null;
	}

	return (
		<OctagonButton
			iconSrc={config.iconSrc}
			badge={config.badge}
			ariaLabel={config.ariaLabel}
			onClick={config.onClick}
			disabled={config.disabled}
			active={config.active}
			interactive={!config.disabled}
			hoverVariant={config.hoverVariant ?? "sidebar-highlight"}
		/>
	);
}

export function HexagonMapControls({
	userZoom,
	onZoomIn,
	onZoomOut,
	onZoomChange,
	isScopedPanelOpen = false,
	onCenterOnPlayer,
	canCenterOnPlayer = false,
	onReturnToMapView,
	canReturnToMapView = false,
	onFastForwardToFurthestStop,
	canFastForwardToFurthestStop = false,
	topLeftOctagonButton,
	topRightOctagonButton,
	thumbMovementControls,
}: HexagonMapControlsProps) {
	const layerClassName = cn(
		isScopedPanelOpen
			? "pointer-events-none z-[5]"
			: "pointer-events-auto z-20",
	);

	return (
		<>
			{topLeftOctagonButton != null ? (
				<div
					className={cn(
						"absolute top-4 left-4 overflow-visible",
						layerClassName,
					)}
				>
					{renderCornerOctagonButton(topLeftOctagonButton)}
				</div>
			) : null}
			{topRightOctagonButton != null ? (
				<div
					className={cn(
						"absolute top-4 right-4 overflow-visible",
						layerClassName,
					)}
				>
					{renderCornerOctagonButton(topRightOctagonButton)}
				</div>
			) : null}
			{thumbMovementControls != null ? (
				<div className={cn("absolute bottom-4 left-4", layerClassName)}>
					<ThumbMovementControls
						disabled={thumbMovementControls.disabled}
						onDriveChange={thumbMovementControls.onDriveChange}
						onDriveEnd={thumbMovementControls.onDriveEnd}
						onPointerInteractionChange={
							thumbMovementControls.onPointerInteractionChange
						}
						syncedVisual={thumbMovementControls.syncedVisual}
					/>
				</div>
			) : null}
			<div
				className={cn(
					"absolute right-4 bottom-4 flex flex-col items-center gap-2",
					layerClassName,
				)}
			>
				{canReturnToMapView && onReturnToMapView ? (
					<HexagonMapCircleActionButton
						ariaLabel="Return to map view"
						onClick={onReturnToMapView}
					>
						<MapIcon className="size-4 shrink-0" aria-hidden />
					</HexagonMapCircleActionButton>
				) : onCenterOnPlayer ? (
					<HexagonMapCircleActionButton
						ariaLabel="Center on your character"
						disabled={!canCenterOnPlayer}
						onClick={onCenterOnPlayer}
					>
						<MapPinIcon className="size-4 shrink-0" aria-hidden />
					</HexagonMapCircleActionButton>
				) : null}
				<div className="flex w-10 flex-col items-center gap-1 rounded-full border border-white/20 bg-black/30 p-1 backdrop-blur-sm">
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="size-8 rounded-full text-white hover:bg-white/10"
						onClick={onZoomIn}
						aria-label="Zoom in"
					>
						<PlusIcon className="size-4" />
					</Button>
					<div className="h-28 w-10 px-1 py-1">
						<Slider
							orientation="vertical"
							value={[userZoom]}
							min={HEXAGON_MAP_MIN_USER_ZOOM}
							max={HEXAGON_MAP_MAX_USER_ZOOM}
							step={HEXAGON_MAP_ZOOM_SLIDER_STEP}
							className="h-full"
							trackClassName="bg-white/20"
							rangeClassName="bg-white/70"
							thumbClassName="border-white/70 bg-white"
							onValueChange={(v) => onZoomChange(v[0] ?? 1)}
							aria-label="Zoom"
						/>
					</div>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="size-8 rounded-full text-white hover:bg-white/10"
						onClick={onZoomOut}
						aria-label="Zoom out"
					>
						<MinusIcon className="size-4" />
					</Button>
				</div>
				{onFastForwardToFurthestStop ? (
					<HexagonMapCircleActionButton
						ariaLabel="Go to furthest unlocked stop"
						disabled={!canFastForwardToFurthestStop}
						onClick={onFastForwardToFurthestStop}
					>
						<FastForwardIcon
							className="size-4 shrink-0"
							aria-hidden
						/>
					</HexagonMapCircleActionButton>
				) : null}
			</div>
		</>
	);
}
